import { useState } from 'react';
import './HelpGuide.css';

export function LeadershipHelpGuide({ onClose }) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to ChamaLink Leadership Dashboard",
      icon: "👋",
      content: "This guide will help you navigate through your group management dashboard. Let's explore the key features available to you as a leader.",
      tips: [
        "You can access this guide anytime by clicking the '?' button",
        "Use the tabs on the left to navigate between sections"
      ]
    },
    {
      title: "Dashboard Overview",
      icon: "📊",
      content: "The Overview tab shows your group's key metrics at a glance including total members, active members, total collected funds, and payment statistics.",
      tips: [
        "Click on any group card to view detailed information",
        "The stats update in real-time as members make payments"
      ]
    },
    {
      title: "Managing Members",
      icon: "👥",
      content: "The Members tab allows you to view, add, edit, and manage all group members. You can change member status, import members in bulk, and export member lists.",
      tips: [
        "Use the status dropdown to set members as Active, Inactive, Probation, or Suspended",
        "Click 'Import Members' to add multiple members via CSV/Excel",
        "Download the CSV template for proper formatting",
        "Export member lists as PDF, CSV, or Excel"
      ]
    },
    {
      title: "Payment Management",
      icon: "💳",
      content: "Track all payments in the Payments tab. You can filter by date, case number, and export payment records for your records.",
      tips: [
        "Use filters to find specific payments",
        "Download payment history as PDF for reporting",
        "Verify manual payments by checking proof uploads"
      ]
    },
    {
      title: "Sending Payment Notices",
      icon: "📢",
      content: "Use the Notices tab to send payment reminders to members. Set deadlines, specify consequences for missed payments, and track notice history.",
      tips: [
        "Set clear deadlines with date and time",
        "Choose consequences: Probation, Penalty, or Inactive status",
        "Members receive email and in-app notifications"
      ]
    },
    {
      title: "Disbursements",
      icon: "💰",
      content: "Process payouts to beneficiaries through the Disbursements tab. All disbursements require approval from designated signatories.",
      tips: [
        "Create disbursement requests with beneficiary details",
        "Track approval status from all signatories",
        "Funds are released only after all approvals"
      ]
    },
    {
      title: "Analytics & Reports",
      icon: "📈",
      content: "View detailed analytics about your group's performance. Filter by time period and download comprehensive PDF reports.",
      tips: [
        "Use preset filters: This Month, Last 30 Days, This Year",
        "Set custom date ranges for specific periods",
        "Download PDF reports for meetings and audits"
      ]
    },
    {
      title: "Group Settings",
      icon: "⚙️",
      content: "Configure your group settings including payment amounts, frequency, bank account for payouts, and notification preferences.",
      tips: [
        "Set up your bank account for receiving disbursements",
        "Configure payment frequency: Weekly, Monthly, or On Demand",
        "Manage group signatories for disbursement approvals"
      ]
    },
    {
      title: "You're All Set!",
      icon: "🎉",
      content: "You now know the basics of managing your group on ChamaLink. Remember, you can always access this guide by clicking the help button.",
      tips: [
        "Need more help? Contact support through the app",
        "Check the Announcements section for platform updates",
        "Switch to Member View to see what your members experience"
      ]
    }
  ];

  const currentStepData = steps[currentStep];

  return (
    <div className="help-guide-overlay" onClick={onClose}>
      <div className="help-guide-modal" onClick={e => e.stopPropagation()}>
        <button className="help-guide-close" onClick={onClose}>×</button>
        
        <div className="help-guide-progress">
          {steps.map((_, index) => (
            <div 
              key={index}
              className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => setCurrentStep(index)}
            />
          ))}
        </div>

        <div className="help-guide-content">
          <div className="help-guide-icon">{currentStepData.icon}</div>
          <h2>{currentStepData.title}</h2>
          <p className="help-guide-description">{currentStepData.content}</p>
          
          <div className="help-guide-tips">
            <h4>💡 Tips:</h4>
            <ul>
              {currentStepData.tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="help-guide-navigation">
          <button 
            className="btn btn-secondary"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
          >
            ← Previous
          </button>
          
          <span className="step-indicator">
            {currentStep + 1} / {steps.length}
          </span>
          
          {currentStep < steps.length - 1 ? (
            <button 
              className="btn btn-primary"
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              Next →
            </button>
          ) : (
            <button 
              className="btn btn-primary"
              onClick={onClose}
            >
              Get Started ✓
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function MemberHelpGuide({ onClose }) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to ChamaLink",
      icon: "👋",
      content: "This guide will help you navigate your member dashboard. Learn how to make payments, manage your wallet, and stay updated with your group.",
      tips: [
        "Access this guide anytime by clicking the '?' button",
        "Use the tabs at the top to navigate between sections"
      ]
    },
    {
      title: "Your Status Overview",
      icon: "🏠",
      content: "The Overview tab shows your membership status, payment information, and quick actions. Keep track of your standing in the group here.",
      tips: [
        "Green status = Active member in good standing",
        "Yellow status = On probation - make payment to restore",
        "View your join date and last payment date",
        "Click 'Make Payment' to pay your contribution"
      ]
    },
    {
      title: "Wallet & Autopay",
      icon: "💰",
      content: "The Wallet tab lets you pre-load funds and enable autopay. When autopay is on, your contributions are automatically paid from your wallet balance.",
      tips: [
        "Top up your wallet using the 'Top Up Wallet' button",
        "Enable Autopay to never miss a payment",
        "You'll be notified when your balance is low",
        "View your transaction history for all wallet activity"
      ]
    },
    {
      title: "Making Payments",
      icon: "💳",
      content: "The Payments tab shows your payment history. You can make new payments, upload proof of manual payments, and download your payment records.",
      tips: [
        "Click 'Make Payment' to pay via card",
        "Use 'Upload Proof' for bank transfers or cash payments",
        "Filter payments by case number or date",
        "Download PDF of your payment history"
      ]
    },
    {
      title: "Notifications",
      icon: "🔔",
      content: "The Notifications tab shows payment reminders and group announcements. Stay informed about deadlines and important updates.",
      tips: [
        "Red badge shows unread notifications",
        "Payment reminders include deadline and consequences",
        "Click 'Make Payment Now' to pay directly from notification",
        "Announcements from leadership appear here too"
      ]
    },
    {
      title: "Meetings",
      icon: "📅",
      content: "The Meetings tab displays upcoming group meetings scheduled by leadership. Never miss an important gathering.",
      tips: [
        "View meeting date, time, and location",
        "Badge shows number of upcoming meetings",
        "Add meetings to your calendar"
      ]
    },
    {
      title: "You're Ready!",
      icon: "🎉",
      content: "You now know how to navigate your member dashboard. Remember to keep your wallet funded if you've enabled autopay!",
      tips: [
        "Enable notifications to stay updated",
        "Contact your group leadership for any questions",
        "Check back regularly for payment reminders"
      ]
    }
  ];

  const currentStepData = steps[currentStep];

  return (
    <div className="help-guide-overlay" onClick={onClose}>
      <div className="help-guide-modal" onClick={e => e.stopPropagation()}>
        <button className="help-guide-close" onClick={onClose}>×</button>
        
        <div className="help-guide-progress">
          {steps.map((_, index) => (
            <div 
              key={index}
              className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => setCurrentStep(index)}
            />
          ))}
        </div>

        <div className="help-guide-content">
          <div className="help-guide-icon">{currentStepData.icon}</div>
          <h2>{currentStepData.title}</h2>
          <p className="help-guide-description">{currentStepData.content}</p>
          
          <div className="help-guide-tips">
            <h4>💡 Tips:</h4>
            <ul>
              {currentStepData.tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="help-guide-navigation">
          <button 
            className="btn btn-secondary"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
          >
            ← Previous
          </button>
          
          <span className="step-indicator">
            {currentStep + 1} / {steps.length}
          </span>
          
          {currentStep < steps.length - 1 ? (
            <button 
              className="btn btn-primary"
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              Next →
            </button>
          ) : (
            <button 
              className="btn btn-primary"
              onClick={onClose}
            >
              Get Started ✓
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
